import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { RoomShellStyle, RsIcon, accentVars, cleanMode } from './roomShellStyle';

// 🪟 The room frame's one sheet (the "+" shelf, People, the phone's Tools,
// a narrow desktop's panels): a centered dialog on desktop, a bottom sheet on
// phones (safe-area padded). RoomSheet's mechanics (Esc, the scrim and ✕
// close it; Tab stays inside; focus returns to whatever opened it), in the
// frame's own tokens so it follows the room's mode (dark / light / auto) and
// accent. Portaled to <body>: room chrome clips, and a fixed layer inside the
// page's invert wrapper breaks in WebKit. z 150010: above the phone ghost sheet
// (150000) and the ♫ button (100010), level with RoomSheet and FilesFolder.

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function RoomShellSheet({
  open, onClose, title, sub = null, isMobile = false, mode = 'dark', accent = null,
  footer = null, children, testId = null, kind = null,
}) {
  const boxRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const titleId = `rs-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  useEffect(() => {
    if (!open) return undefined;
    const opener = typeof document !== 'undefined' ? document.activeElement : null;
    const t = setTimeout(() => {
      const box = boxRef.current;
      if (!box || box.contains(document.activeElement)) return;
      box.focus({ preventScroll: true });
    }, 30);
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); if (closeRef.current) closeRef.current(); return; }
      if (e.key !== 'Tab' || !boxRef.current) return;
      const list = [...boxRef.current.querySelectorAll(FOCUSABLE)].filter((el) => el.getAttribute('tabindex') !== '-1');
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === boxRef.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey, true);
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
        try { opener.focus({ preventScroll: true }); } catch (e) { /* gone */ }
      }
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="rs-layer"
      data-rs-mode={cleanMode(mode)}
      data-phone={isMobile ? '1' : '0'}
      data-rs-sheet={kind || undefined}
      style={accentVars(accent)}
      onMouseDown={(e) => { if (e.target === e.currentTarget && closeRef.current) closeRef.current(); }}
    >
      <RoomShellStyle />
      <div ref={boxRef} className="rs-sh" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} data-testid={testId || undefined}>
        {isMobile ? <i className="rs-grab" aria-hidden="true" /> : null}
        <div className="rs-shtop">
          <div className="rs-shtw">
            <div className="rs-sht" id={titleId} role="heading" aria-level={2}>{title}</div>
            {sub ? <div className="rs-shs">{sub}</div> : null}
          </div>
          <button type="button" className="rs-x" data-prop-ui="" aria-label="Close" onClick={() => { if (closeRef.current) closeRef.current(); }}>
            <RsIcon name="x" size={18} />
          </button>
        </div>
        <div className="rs-shb">{children}</div>
        {footer ? <div className="rs-shf">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
